import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
// import { FaChevronDown } from 'react-icons/fa6';

import { KeepType, useResolveConflict } from 'hooks/cad/useResolveConflict';

import { castArray, isEmpty, isEqual, isNil, omitBy } from 'lodash';
import {
  LoadingWrapper,
  UploadingOverlay,
} from 'libs/editor/plugins/nodes/blocks/preview';

import { GltfLoading } from 'libs/rendering/gltf/components/loading';
import { ConflictAlertHeight } from 'styles/constants';
import { MessageType } from 'constants/global';

import { useRouterStore } from 'apps/hooks';
import {
  closeTabById,
  getCurrentTabIdFromDesktop,
  gutResolve,
  isInDesktopMode,
} from 'libs/gutClient/electronBridge';
import i18next from 'i18next';
import Alert from 'react-s-alert';
import { analytics } from 'core/analytics';
import { useCADManager } from 'hooks/cad';
import { frameStore } from 'apps/components/Frame/appManager';
import { nilKeys } from 'utils';
import { useViewerWidth } from 'libs/rendering/hooks';
import { Separator } from './canvas3D.styled';
import {
  ConflictButton,
  ProjectFileDiffBar,
  ShareFileDiffBar,
  OpenCADRooms,
} from './components';

// type TwoSidedGltfDiffProps = {};

export const TwoSidedGltfDiff = observer(() => {
  const routerStore = useRouterStore();
  const { route, navigate } = routerStore;

  const {
    repoId,
    diff,
    diffV,
    sharedFileId,
    v,

    // projects route
    slug,
    initiative,
    profile,
    basepath = '',
    // tabId,
    selectedKeys,
    conflict,
    isCloudDiffFile,
    contributionId,
  } = route.params;

  const { viewerControl, startDiffing } = useCADManager();
  const { viewer, file, contextManager } = viewerControl;
  const { diffFile: rightFile, file: leftFile } = viewerControl || {};
  const [selectedFileIds, setSelectedFileIds] = useState([]);

  const isSamePath =
    rightFile && leftFile && isEqual(rightFile.filename, leftFile.filename);

  const hasDiff = !!repoId && diff;
  const hasConflict = hasDiff && conflict;

  const showResolveConflictLoading =
    !viewer?.isDiffLoading && diff && !rightFile;

  const postConflictMessage = async () => {
    window.postMessage({
      refreshConflictData: true,
      type: MessageType.refreshConflictData,
    });
  };
  const handleConflictSolved = async () => {
    await postConflictMessage();

    if (isInDesktopMode()) {
      const closeTabId = await getCurrentTabIdFromDesktop();
      await closeTabById({
        id: closeTabId,
        needNotifyView: true,
      });

      return;
    }

    // just project page has conflict.
    const newParams = omitBy(
      {
        slug,
        profile,
        initiative,
      },
      isNil,
    );

    const projectFilesRouteName = `${
      initiative ? 'initiative' : 'profile'
    }.project.files`;

    navigate(projectFilesRouteName, newParams);
  };

  const handleResolveConflictError = (error: any) => {
    analytics.logError(error);
    Alert.error(i18next.t(error?.message || 'Conflict resolution failed'));
  };

  const keepCloudClick = async (path: string) => {
    // Ideally this should call a syncRepoAppManager, but right now it does not
    // exist in this route, we shouldn't initialize a new one here, needs to
    // exist in a parent container. As more syncRepoAppManager features are needed
    // in various non-project routes we'll make a proper global syncRepoAppManager
    // instance.
    const [, error] = await gutResolve(repoId, 'Theirs', [path]);
    if (error) {
      handleResolveConflictError(error);
      return;
    }
    setSelectedFileIds(selectedFileIds.concat(leftFile.id));
    await handleConflictSolved();
  };

  const keepLocalClick = async (path: string) => {
    // Same as above, this is duplication of code to be consolidated in the future.
    const [, error] = await gutResolve(repoId, 'Ours', [path]);
    if (error) {
      handleResolveConflictError(error);
      return;
    }
    setSelectedFileIds(selectedFileIds.concat(rightFile.id));
    await handleConflictSolved();
  };

  const { confirmModal, openConfirmModal } = useResolveConflict({
    filename: file?.filename,
    onKeepCloud: async () => {
      await keepCloudClick(basepath);
    },
    onKeepLocal: async () => {
      await keepLocalClick(basepath);
    },
  });

  const handleDiffVersion = (partialParams: any) => {
    navigate(route.name, {
      ...route.params,
      ...partialParams,
    });
    startDiffing();
  };

  const { sharedFileManager } = frameStore;
  const revisionsConnection = sharedFileManager?.revisionsConn;
  const isSharedFileRoute = route.name.endsWith('shared.file');
  const shareRevisions = revisionsConnection?.arrayApp?.apps || [];

  const leftSelectedVersion = v || shareRevisions[0]?.version;

  const leftActiveVersion =
    shareRevisions.find((r: any) => r.version === leftSelectedVersion) ||
    shareRevisions[0];
  const rightActiveVersion = shareRevisions.find(
    (r: any) => r.version === diffV,
  );

  const diffVersions = [diffV, leftActiveVersion?.version];

  const spacePathParameter = initiative ? { initiative } : { profile };

  const leftJumpParams = isSharedFileRoute
    ? {
        ...spacePathParameter,
        sharedFileId,
        v: leftActiveVersion?.version,
      }
    : {
        ...spacePathParameter,
        slug,
        basepath,
      };
  if (contributionId) (leftJumpParams as any).contributionId = contributionId;

  const rightJumpParams = isSharedFileRoute
    ? {
        ...spacePathParameter,
        sharedFileId,
        v: rightActiveVersion?.version,
      }
    : {
        // TODO:@kejuqu add new router for local file
      };

  const resolvedFileIds = (
    selectedFileIds?.length
      ? selectedFileIds
      : castArray(selectedKeys?.split(',')) || []
  ).filter((fileId: string) => !nilKeys.includes(fileId) && !isNil(fileId));

  const latestUpdatedBy =
    contextManager?.contextAppHolder?.contribFile?.contribution
      ?.creatorProfile || {};

  const isSelectedLeft = leftFile && resolvedFileIds.includes(leftFile.id);
  const isSelectedRight = rightFile && resolvedFileIds.includes(rightFile.id);

  const [canvasWidth /* , rightOffset */] = useViewerWidth();

  return (
    <>
      {hasConflict && leftFile && (
        <div
          className=' w-full border-b border-wfGrey200 bg-white py-2 text-center absolute top-0 left-0 z-[10000] text-wfBlack font-bold pointer-events-none'
          style={{
            height: ConflictAlertHeight,
          }}
        >
          Please select the file you want to keep
        </div>
      )}
      <Separator
        width={canvasWidth}
        data-testid='CADPage-DiffSeparator'
        top={hasConflict ? ConflictAlertHeight : 0}
      >
        {leftFile && (
          <>
            {isSharedFileRoute ? (
              <ShareFileDiffBar
                options={shareRevisions}
                isBaseFile
                activeRevision={leftActiveVersion}
                openFile={leftFile}
                onClickOptionItem={versionItem => {
                  handleDiffVersion({
                    v: versionItem.version,
                  });
                }}
                diffVersions={diffVersions}
              />
            ) : (
              <ProjectFileDiffBar
                isBaseFile
                openFile={leftFile}
                username={latestUpdatedBy.username}
              />
            )}
            <OpenCADRooms
              name={route.name}
              filename={leftActiveVersion?.file?.filename || leftFile?.filename}
              // openVersion={leftActiveVersion}
              params={leftJumpParams}
              style={{
                width: '100%',
              }}
            />
            {(hasConflict || !isEmpty(resolvedFileIds)) && (
              <ConflictButton
                icon='arrow_right'
                isSelected={isSelectedLeft}
                hide={!isEmpty(resolvedFileIds) && !isSelectedLeft}
                onClick={() => {
                  openConfirmModal(KeepType.Cloud);
                }}
              />
            )}
          </>
        )}
      </Separator>
      {/* diff section */}
      {(rightFile || hasConflict || showResolveConflictLoading) && (
        <Separator
          width={canvasWidth}
          top={hasConflict ? ConflictAlertHeight : 0}
          style={{
            borderRight: 'none',
            left: `calc(${canvasWidth} /2 + 0.5px)`,
          }}
        >
          {showResolveConflictLoading && (
            <UploadingOverlay>
              <LoadingWrapper>
                <GltfLoading text='Loading...' visible />
              </LoadingWrapper>
            </UploadingOverlay>
          )}
          {rightFile && (
            <>
              {isSharedFileRoute ? (
                <ShareFileDiffBar
                  options={shareRevisions}
                  openFile={rightFile}
                  activeRevision={rightActiveVersion}
                  onClickOptionItem={versionItem => {
                    handleDiffVersion({
                      diff: versionItem.file.id,
                      diffV: versionItem.version,
                    });
                  }}
                  diffVersions={diffVersions}
                />
              ) : (
                <ProjectFileDiffBar
                  cannotView={isSamePath}
                  openFile={rightFile}
                  isCloudDiffFile={isCloudDiffFile}
                />
              )}
              {isSharedFileRoute && (
                <OpenCADRooms
                  name={route.name}
                  filename={rightActiveVersion?.file?.filename}
                  // openVersion={rightActiveVersion}
                  params={rightJumpParams}
                />
              )}
              {(hasConflict || !isEmpty(resolvedFileIds)) && (
                <ConflictButton
                  icon='arrow_right'
                  isSelected={isSelectedRight}
                  hide={!isEmpty(resolvedFileIds) && !isSelectedRight}
                  onClick={() => {
                    openConfirmModal(KeepType.Local);
                  }}
                />
              )}
            </>
          )}
        </Separator>
      )}
      {confirmModal}
    </>
  );
});
